import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PagoManualDialog } from "@/components/PagoManualDialog";
import { BonoHistoricoDialog } from "@/components/BonoHistoricoDialog";
import { toast } from "sonner";
import { Banknote, Trash2, Image as ImageIcon, History } from "lucide-react";

export const PagosManuales = ({ alumnoId, sedeId, onCambio }) => {
  const [pagos, setPagos] = useState([]);
  const [open, setOpen] = useState(false);
  const [histOpen, setHistOpen] = useState(false);

  const cargar = () => api.get(`/alumnos/${alumnoId}/pagos-manuales`)
    .then(({ data }) => setPagos(data)).catch(() => {});
  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [alumnoId]);

  const anular = async (sid) => {
    if (!window.confirm("¿Anular este pago? La ficha volverá al estado anterior y la factura quedará anulada.")) return;
    try {
      await api.delete(`/pagos-manuales/${sid}`);
      toast.success("Pago anulado");
      cargar();
      onCambio?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="card-pagos-manuales">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display text-lg font-bold">Pagos en la academia</h3>
          <p className="text-xs text-muted-foreground mt-1">Efectivo o transferencia, con foto del recibo</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" className="rounded-full font-semibold" onClick={() => setOpen(true)}
            data-testid="registrar-pago-manual-btn">
            <Banknote className="h-4 w-4 mr-2" /> Registrar pago
          </Button>
          <Button variant="outline" size="sm" className="rounded-full font-semibold"
            onClick={() => setHistOpen(true)} data-testid="bono-historico-btn">
            <History className="h-4 w-4 mr-2" /> Bono ya contratado
          </Button>
        </div>
      </div>

      {pagos.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="pagos-manuales-empty">
          Todavía no hay pagos registrados a mano para este alumno.
        </p>
      )}
      <div className="space-y-3">
        {pagos.map((p) => (
          <div key={p.session_id} data-testid={`pago-manual-${p.session_id}`}
            className={`rounded-xl border p-3 ${p.anulado ? "opacity-60 border-dashed" : "border-border"}`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold">{p.plan_nombre || "Pago"} · {Number(p.importe).toFixed(2)} €</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {p.metodo_label} · {p.pendiente ? "sin cobrar" : `cobrado el ${p.fecha_pago}`}
                  {p.fecha_inicio ? ` · desde el ${p.fecha_inicio}` : ""}
                  {p.factura ? ` · factura ${p.factura}` : ""}
                </p>
                {p.nota && <p className="text-xs text-muted-foreground mt-1">{p.nota}</p>}
                {p.recibo_file_id && (
                  <a href={fileUrl(p.recibo_file_id)} target="_blank" rel="noreferrer"
                    data-testid={`recibo-${p.session_id}`}
                    className="text-xs font-semibold text-primary inline-flex items-center gap-1 mt-1.5 hover:underline">
                    <ImageIcon className="h-3.5 w-3.5" /> Ver recibo
                  </a>
                )}
              </div>
              {p.anulado ? (
                <Badge variant="secondary">Anulado</Badge>
              ) : p.pendiente ? (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-amber-600 border-amber-500/40"
                    data-testid={`pago-pendiente-${p.session_id}`}>Pendiente de cobro</Badge>
                  <button onClick={() => anular(p.session_id)} data-testid={`anular-pago-${p.session_id}`}
                    title="Descartar cobro pendiente">
                    <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive transition-colors duration-200" />
                  </button>
                </div>
              ) : (
                <button onClick={() => anular(p.session_id)} data-testid={`anular-pago-${p.session_id}`}
                  title="Anular pago">
                  <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive transition-colors duration-200" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <PagoManualDialog open={open} onOpenChange={setOpen} alumnoId={alumnoId} sedeId={sedeId}
        onHecho={() => { cargar(); onCambio?.(); }} />
      <BonoHistoricoDialog open={histOpen} onOpenChange={setHistOpen} alumnoId={alumnoId}
        sedeId={sedeId} onHecho={() => { cargar(); onCambio?.(); }} />
    </Card>
  );
};
